import { useOptions, useUser } from '../hooks';
import { actions } from '../models';
import { useAppDispatch, useAppSelector } from '../store';
import type { NomineeTrackingParams, NomineeType, PollType } from '../types';
import { pushUserVote } from '../utils/apis';
import { showLogin } from '../utils/IdpUtils';
import LocalStorageClient from '../utils/LocalStorageClient';

/**
 * Custom hook for managing the state and behavior of the vote "like" button.
 *
 * @param {PollType} poll - The poll object.
 * @param {NomineeType} nominee - The nominee object.
 * @returns {Object} The state and behavior of the "like" button.
 */
export const useLikeButtonVote = (poll: PollType, nominee: NomineeType) => {
  const { user, isAuthenticated } = useUser();
  const sponsor = useAppSelector((state) => state.sponsors?.sponsor);
  const dispatch = useAppDispatch();
  const { enableLoginFlow, appType, promoTag, competitionBanner } = useOptions();

  const vote = async (trackingFunction: (params: NomineeTrackingParams) => void, htmlEl?: HTMLElement) => {
    if (!isAuthenticated && enableLoginFlow && !promoTag) {
      showLogin();
    }

    trackingFunction({
      poll,
      nominee,
      appType,
      competition: window.competitionTracking ?? competitionBanner,
      sponsor,
      user,
      htmlEl,
    });

    user && (await pushUserVote(poll, nominee, user));

    LocalStorageClient.trackVote(poll, nominee, user);
    dispatch(actions.user.setUserVoted(poll));

    if (promoTag) {
      dispatch(actions.prizeModal.setVisible(true));
    } else {
      dispatch(actions.loginModal.setVisible(true));
    }
  };

  return { vote };
};
